'use client'
import { Player } from "@lottiefiles/react-lottie-player";
import { PrismicImage, PrismicRichText } from "@prismicio/react";
import { useEffect, useRef } from "react";

/**
 * @typedef {import("@prismicio/client").Content.StepsSlice} StepsSlice
 * @typedef {import("@prismicio/react").SliceComponentProps<StepsSlice>} StepsProps
 * @param {StepsProps}
 */
const Arch = ({ slice, index }) => {
  return <section
    data-slice-type={slice.slice_type}
    data-slice-variation={slice.variation}
    id="step-section"
    className={`bg-${slice.primary.bg_color} ${index !== 0 ? 'border-t-2 border-dark-grey' : ''} bg-grain overflow-hidden`}>
    <div className="pb-[54px] pt-[87px] md:pb-20 md:pt-[100px] px-4 md:px-7 lg:px-10 relative z-[10] max-w-[1400px] m-auto flex flex-col lg:flex-row gap-10 lg:gap-20">
      <div className="lg:w-[60%]">
        <PrismicRichText field={slice.primary.title} />
        <div className="flex flex-col mt-16 gap-6 lg:gap-14">
          {
            slice.primary.steps.map((step, i) => <div key={i} className="flex flex-row gap-4">
              <div className="hidden steps-text lg:flex items-center justify-center w-20 h-20 bg-white rounded-full aspect-square border-2 border-dark-grey lg:-mt-5">
                {step.number}</div>
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <div className="lg:hidden steps-litle-text flex items-center justify-center w-12 h-12 bg-white rounded-full aspect-square border-2 border-dark-grey">
                    {step.number}</div>
                  <PrismicRichText field={step.title} />
                </div>
                <PrismicRichText field={step.text} />
              </div>
            </div>
            )
          }
        </div>

      </div>
      <div className="hidden lg:block overflow-hidden rounded-t-full border-2 border-dark-grey w-[40%]" >
        <PrismicImage field={slice.primary.picture_desktop} className="object-cover h-full w-full"  loading={index === 0 ? '' : 'lazy'}/>
      </div>
      <div className="lg:hidden overflow-hidden rounded-l-full border-2 border-dark-grey" >
        <PrismicImage field={slice.primary.picture} className="object-cover h-[250px] md:h-[350px] w-full"  loading={index === 0 ? '' : 'lazy'}/>
      </div>
    </div>
  </section>
}

/**
 * @typedef {import("@prismicio/client").Content.StepsSlice} StepsSlice
 * @typedef {import("@prismicio/react").SliceComponentProps<StepsSlice>} StepsProps
 * @param {StepsProps}
 */
const Default = ({ slice, index }) => {
  const player = useRef();

  useEffect(() => {
    const $stepSection = document.getElementById('step-section');
    if ($stepSection != null) observeStep($stepSection)
  })

  function observeStep(elem) {
    const obs = {
      rootMargin: '', threshold: .5
    }
    function cb(entries) {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          player.current.play()
        }
      });
    };
    const observer = new IntersectionObserver(cb, obs)
    observer.observe(elem);
  }

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      id="step-section" className={`bg-white bg-grain flex flex-col overflow-hidden ${index !== 0 ? 'border-t-2 border-dark-grey' : ''}`}>
      <div className="pt-[40px] pb-16 px-4 md:px-7 lg:px-0 lg:pb-0 md:py-20 lg:pt-[100px] flex flex-col-reverse text-dark-grey">
        <div className="flex lg:mr-10">
          <div className="relative hidden lg:block translate-y-px mt-4  h-[465px]">
            <div id="stairs-home" className="w-[455px] h-auto">
              <Player
                ref={player}
                autoplay={false}
                loop={false}
                keepLastFrame
                src="/anim/anim-stairs.json">
              </Player>
            </div>
            <div className="absolute top-[6px] -z-[1]">
              <svg viewBox="0 0 0 0" fill="none" xmlns="http://www.w3.org/2000/svg" className="absolute">
                <clipPath id="img-stairs">
                  <path
                    d="M-100.555 458V0.456055H82.1699V91.939H173.5V183.487H264.83V274.969H356.225V366.452H447.555V458"
                    fill="#F9E1D6" />     
                    
                </clipPath>
              </svg>
              <PrismicImage
                    field={slice.primary.bg_picture}
                    className="object-cover h-[465px] w-[535px]"
                    style={{ clipPath: 'url(#img-stairs)' }}  loading="lazy"/>
            </div>
          </div>
          <div className="w-full md:max-w-[600px]">
            <div className="lg:-ml-[320px] flex items-center mb-[35px]">
              <p className="steps-text mr-3.5">
                {slice.primary.steps[0].number}
              </p>
              <PrismicRichText field={slice.primary.steps[0].text} />
            </div>
            <div className="lg:-ml-[228px] flex items-center mb-[35px]">
              <p className="steps-text mr-3.5">
                {slice.primary.steps[1].number}</p>
              <PrismicRichText field={slice.primary.steps[1].text} />
            </div>
            <div className="lg:-ml-[136px] flex items-center mb-[35px]">
              <p className="steps-text mr-3.5">
                {slice.primary.steps[2].number}</p>
              <PrismicRichText field={slice.primary.steps[2].text} />
            </div>
            <div className="lg:-ml-[44px] flex items-center mb-[35px]">
              <p className="steps-text mr-3.5">
                {slice.primary.steps[3].number}</p>
              <PrismicRichText field={slice.primary.steps[3].text} />
            </div>
            <div className="lg:ml-[48px] flex items-center">
              <p className="steps-text mr-3.5">
                {slice.primary.steps[4].number}</p>
              <PrismicRichText field={slice.primary.steps[4].text} />
            </div>
          </div>
        </div>
        <div className="mb-11 h2-like lg:mb-[100px] lg:px-10 md:text-center">
          <PrismicRichText field={slice.primary.title} />
        </div>
      </div>
    </section>
  );
};

const Steps = ({ slice }) => slice.variation === "default" ? <Default slice={slice} /> : <Arch slice={slice} />

export default Steps;
